/**
 * <Select />
 *
 * Native <select> wrapped to match the design system. We deliberately use
 * the browser's native control here (instead of Radix or a custom popover)
 * because:
 *   * The mobile OS picker UX is ideal for senior users (giant touch list).
 *   * Zero extra bundle weight.
 *   * Accessible by default.
 *
 * Error + hint semantics mirror <Input /> exactly so forms feel coherent.
 */
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/cn";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label: string;
  hideLabel?: boolean;
  hint?: string;
  error?: string;
  /** Options rendered as <option>s. Use native children for groups if needed. */
  options?: SelectOption[];
  /** If true, renders a leading placeholder option (disabled + selected by default). */
  placeholder?: string;
  sizeVariant?: "md" | "lg";
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      hideLabel,
      hint,
      error,
      options,
      placeholder,
      id,
      className,
      sizeVariant = "md",
      children,
      defaultValue,
      value,
      ...rest
    },
    ref,
  ) => {
    const reactId = React.useId();
    const selectId = id ?? `select-${reactId}`;
    const describedById = hint || error ? `${selectId}-desc` : undefined;
    const hasError = Boolean(error);
    const heightClass = sizeVariant === "lg" ? "h-14 text-lg" : "h-12 text-base";

    return (
      <div className="w-full">
        <label
          htmlFor={selectId}
          className={cn(
            "mb-1.5 block font-semibold text-ink",
            hideLabel ? "sr-only" : "text-sm",
          )}
        >
          {label}
        </label>

        <div
          className={cn(
            "relative flex items-center rounded-xl border-2 bg-white transition-colors",
            heightClass,
            hasError ? "border-brand" : "border-surface-border focus-within:border-ink",
          )}
        >
          <select
            ref={ref}
            id={selectId}
            aria-invalid={hasError || undefined}
            aria-describedby={describedById}
            defaultValue={defaultValue ?? (placeholder && value === undefined ? "" : undefined)}
            value={value}
            className={cn(
              "appearance-none w-full h-full bg-transparent pl-3 pr-10 outline-none text-ink",
              "disabled:cursor-not-allowed disabled:opacity-50",
              className,
            )}
            {...rest}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((o) => (
                  <option key={o.value} value={o.value} disabled={o.disabled}>
                    {o.label}
                  </option>
                ))
              : children}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-3 h-5 w-5 text-ink-muted"
            aria-hidden="true"
          />
        </div>

        {(hint || error) && (
          <p
            id={describedById}
            className={cn(
              "mt-1.5 text-sm",
              hasError ? "text-brand font-medium" : "text-ink-muted",
            )}
          >
            {error ?? hint}
          </p>
        )}
      </div>
    );
  },
);
Select.displayName = "Select";
